import { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../config/firebase';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import airportImage from '../../assets/airportt.png';
import { Shield, Upload, X, FileText, CheckCircle, Car } from 'lucide-react';

const formatDate = value => {
  if (!value) return null;
  const date =
    value?.toDate?.() ??
    (typeof value === 'number' || typeof value === 'string'
      ? new Date(value)
      : value);
  if (!date || Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, initializing } = usePublicAuth();
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState({
    senderRequests: 0,
    travelerRequests: 0,
    senderCompleted: 0,
    travelerCompleted: 0,
  });
  const [loading, setLoading] = useState(true);
  const [verificationType, setVerificationType] = useState(null); // 'passport', 'idCard', 'driverLicence'
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [frontPreview, setFrontPreview] = useState(null);
  const [backPreview, setBackPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const frontFileInputRef = useRef(null);
  const backFileInputRef = useRef(null);
  
  // Driver verification states
  const [driverLicenseFront, setDriverLicenseFront] = useState(null);
  const [driverLicenseBack, setDriverLicenseBack] = useState(null);
  const [vehicleInsurance, setVehicleInsurance] = useState(null);
  const [driverLicenseFrontPreview, setDriverLicenseFrontPreview] = useState(null);
  const [driverLicenseBackPreview, setDriverLicenseBackPreview] = useState(null);
  const [vehicleInsurancePreview, setVehicleInsurancePreview] = useState(null);
  const [uploadingDriverVerification, setUploadingDriverVerification] = useState(false);
  const [submittingDriverVerification, setSubmittingDriverVerification] = useState(false);
  const driverLicenseFrontInputRef = useRef(null);
  const driverLicenseBackInputRef = useRef(null);
  const vehicleInsuranceInputRef = useRef(null);
  const [isRideMode, setIsRideMode] = useState(false);

  // Check ride mode from localStorage
  useEffect(() => {
    const checkRideMode = () => {
      const savedMode = localStorage.getItem('flexySwap_activeMode');
      setIsRideMode(savedMode === 'flexyRide');
    };
    
    checkRideMode();
    window.addEventListener('storage', checkRideMode);
    window.addEventListener('flexySwap_modeChange', checkRideMode);
    
    return () => {
      window.removeEventListener('storage', checkRideMode);
      window.removeEventListener('flexySwap_modeChange', checkRideMode);
    };
  }, []);

  useEffect(() => {
    if (!user?.uid) {
      setProfile(null);
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setProfile({ id: userDoc.id, ...data });
          // Load verification file previews if they exist (only if not verified)
          if (data.verificationStatus !== 'verified') {
            if (data.verificationFileUrl) {
              setFrontPreview(data.verificationFileUrl);
            }
            if (data.verificationBackFileUrl) {
              setBackPreview(data.verificationBackFileUrl);
            }
            if (data.verificationFileType) {
              setVerificationType(data.verificationFileType);
            }
          }
          
          // Load driver verification file previews if they exist
          if (data.driverLicenseFrontUrl) {
            setDriverLicenseFrontPreview(data.driverLicenseFrontUrl);
          }
          if (data.driverLicenseBackUrl) {
            setDriverLicenseBackPreview(data.driverLicenseBackUrl);
          }
          if (data.vehicleInsuranceUrl) {
            setVehicleInsurancePreview(data.vehicleInsuranceUrl);
          }
        } else {
          setProfile(null);
        }

        const requesterSnap = await getDocs(
          query(
            collection(db, 'requests'),
            where('requesterId', '==', user.uid),
          ),
        );
        const ownerSnap = await getDocs(
          query(
            collection(db, 'requests'),
            where('tripOwnerId', '==', user.uid),
          ),
        );

        let senderCompleted = 0;
        let travelerCompleted = 0;

        requesterSnap.docs.forEach(docSnap => {
          const data = docSnap.data();
          if ((data.status || '').toLowerCase() === 'completed') {
            if (data.tripType === 'shipping') senderCompleted += 1;
            if (data.tripType === 'carrying') travelerCompleted += 1;
          }
        });

        ownerSnap.docs.forEach(docSnap => {
          const data = docSnap.data();
          if ((data.status || '').toLowerCase() === 'completed') {
            if (data.tripType === 'carrying') senderCompleted += 1;
            if (data.tripType === 'shipping') travelerCompleted += 1;
          }
        });

        setStats({
          senderRequests: requesterSnap.size,
          travelerRequests: ownerSnap.size,
          senderCompleted,
          travelerCompleted,
        });
      } catch (error) {
        console.error('Profile load error:', error);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user?.uid]);

  const displayName =
    profile?.displayName ||
    user?.displayName ||
    user?.email ||
    t('profile.userFallback');
  const profilePhoto =
    profile?.photoURL ||
    profile?.avatar ||
    user?.photoURL ||
    user?.profile?.photoURL ||
    user?.avatar ||
    user?.photo ||
    null;
  const initials = useMemo(
    () =>
      displayName
        .split(' ')
        .map(part => part.charAt(0).toUpperCase())
        .slice(0, 2)
        .join(''),
    [displayName],
  );

  const handleSubmitVerification = async () => {
    if (!user?.uid || !frontFile || !backFile || !verificationType) return;

    setSubmitting(true);
    try {
      // Upload front file
      const frontTimestamp = Date.now();
      const frontExtension = frontFile.name.split('.').pop();
      const frontFileName = `${verificationType}_front_${user.uid}_${frontTimestamp}.${frontExtension}`;
      const frontStoragePath = `verification/${user.uid}/${frontFileName}`;
      const frontStorageRef = ref(storage, frontStoragePath);

      setUploading(true);
      await uploadBytes(frontStorageRef, frontFile);
      const frontDownloadUrl = await getDownloadURL(frontStorageRef);

      // Upload back file
      const backTimestamp = Date.now();
      const backExtension = backFile.name.split('.').pop();
      const backFileName = `${verificationType}_back_${user.uid}_${backTimestamp}.${backExtension}`;
      const backStoragePath = `verification/${user.uid}/${backFileName}`;
      const backStorageRef = ref(storage, backStoragePath);

      await uploadBytes(backStorageRef, backFile);
      const backDownloadUrl = await getDownloadURL(backStorageRef);

      setUploading(false);

      // Update Firestore
      await updateDoc(doc(db, 'users', user.uid), {
        verificationFileUrl: frontDownloadUrl,
        verificationFileName: frontFile.name,
        verificationBackFileUrl: backDownloadUrl,
        verificationBackFileName: backFile.name,
        verificationFileType: verificationType,
        verificationStatus: 'pending',
        verificationSubmittedAt: serverTimestamp(),
      });

      // Update local state
      setProfile(prev => ({
        ...prev,
        verificationFileUrl: frontDownloadUrl,
        verificationFileName: frontFile.name,
        verificationBackFileUrl: backDownloadUrl,
        verificationBackFileName: backFile.name,
        verificationFileType: verificationType,
        verificationStatus: 'pending',
      }));

      // Reset form
      setFrontFile(null);
      setBackFile(null);
      setFrontPreview(null);
      setBackPreview(null);
      if (frontFileInputRef.current) frontFileInputRef.current.value = '';
      if (backFileInputRef.current) backFileInputRef.current.value = '';

      alert(
        'Verification documents submitted successfully! Your documents are under review.',
      );
    } catch (error) {
      console.error('Error submitting verification:', error);
      alert('Failed to submit verification documents. Please try again.');
    } finally {
      setUploading(false);
      setSubmitting(false);
    }
  };

  const handleSubmitDriverVerification = async () => {
    if (!user?.uid || !driverLicenseFront || !driverLicenseBack || !vehicleInsurance) return;

    setSubmittingDriverVerification(true);
    try {
      // Upload driver license front
      const frontTimestamp = Date.now();
      const frontExtension = driverLicenseFront.name.split('.').pop();
      const frontFileName = `driver_license_front_${user.uid}_${frontTimestamp}.${frontExtension}`;
      const frontStoragePath = `driver_verification/${user.uid}/${frontFileName}`;
      const frontStorageRef = ref(storage, frontStoragePath);

      setUploadingDriverVerification(true);
      await uploadBytes(frontStorageRef, driverLicenseFront);
      const frontDownloadUrl = await getDownloadURL(frontStorageRef);

      // Upload driver license back
      const backTimestamp = Date.now();
      const backExtension = driverLicenseBack.name.split('.').pop();
      const backFileName = `driver_license_back_${user.uid}_${backTimestamp}.${backExtension}`;
      const backStoragePath = `driver_verification/${user.uid}/${backFileName}`;
      const backStorageRef = ref(storage, backStoragePath);

      await uploadBytes(backStorageRef, driverLicenseBack);
      const backDownloadUrl = await getDownloadURL(backStorageRef);

      // Upload vehicle insurance
      const insuranceTimestamp = Date.now();
      const insuranceExtension = vehicleInsurance.name.split('.').pop();
      const insuranceFileName = `vehicle_insurance_${user.uid}_${insuranceTimestamp}.${insuranceExtension}`;
      const insuranceStoragePath = `driver_verification/${user.uid}/${insuranceFileName}`;
      const insuranceStorageRef = ref(storage, insuranceStoragePath);

      await uploadBytes(insuranceStorageRef, vehicleInsurance);
      const insuranceDownloadUrl = await getDownloadURL(insuranceStorageRef);

      setUploadingDriverVerification(false);

      // Update Firestore
      await updateDoc(doc(db, 'users', user.uid), {
        driverLicenseFrontUrl: frontDownloadUrl,
        driverLicenseFrontName: driverLicenseFront.name,
        driverLicenseBackUrl: backDownloadUrl,
        driverLicenseBackName: driverLicenseBack.name,
        vehicleInsuranceUrl: insuranceDownloadUrl,
        vehicleInsuranceName: vehicleInsurance.name,
        driverVerificationStatus: 'pending',
        driverVerificationSubmittedAt: serverTimestamp(),
      });

      // Update local state
      setProfile(prev => ({
        ...prev,
        driverLicenseFrontUrl: frontDownloadUrl,
        driverLicenseFrontName: driverLicenseFront.name,
        driverLicenseBackUrl: backDownloadUrl,
        driverLicenseBackName: driverLicenseBack.name,
        vehicleInsuranceUrl: insuranceDownloadUrl,
        vehicleInsuranceName: vehicleInsurance.name,
        driverVerificationStatus: 'pending',
      }));

      // Reset form
      setDriverLicenseFront(null);
      setDriverLicenseBack(null);
      setVehicleInsurance(null);
      setDriverLicenseFrontPreview(null);
      setDriverLicenseBackPreview(null);
      setVehicleInsurancePreview(null);
      if (driverLicenseFrontInputRef.current) driverLicenseFrontInputRef.current.value = '';
      if (driverLicenseBackInputRef.current) driverLicenseBackInputRef.current.value = '';
      if (vehicleInsuranceInputRef.current) vehicleInsuranceInputRef.current.value = '';

      alert(
        'Driver verification documents submitted successfully! Your documents are under review.',
      );
    } catch (error) {
      console.error('Error submitting driver verification:', error);
      alert('Failed to submit driver verification documents. Please try again.');
    } finally {
      setUploadingDriverVerification(false);
      setSubmittingDriverVerification(false);
    }
  };

  if (!user && !initializing) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />
        <main className="mx-auto max-w-4xl px-4 pt-32 pb-16 text-center">
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-10 shadow-xl border border-gray-100 dark:border-slate-800">
            <h1 className="text-3xl font-black text-gray-900 dark:text-slate-100 mb-4">
              {t('profile.title')}
            </h1>
            <p className="text-gray-600 dark:text-slate-300 mb-6">
              {t('profile.loginRequired')}
            </p>
            <button
              onClick={() => navigate('/auth/login')}
              className="inline-flex items-center justify-center rounded-2xl bg-black px-6 py-3 text-sm font-semibold text-yellow-400 transition hover:bg-gray-900 dark:hover:bg-slate-800"
            >
              {t('incomingRequests.goToLogin')}
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <MainNavbar />

      <section className="relative bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 dark:from-slate-800 dark:via-slate-900 dark:to-black overflow-hidden pt-28 pb-16 min-h-[420px]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.4em] text-yellow-200">
                {t('profile.heroBadge')}
              </p>
              <h1 className="text-5xl md:text-6xl font-black text-white mb-4 drop-shadow-lg">
                {t('profile.heroTitle', {
                  name:
                    displayName.split(' ')[0] || t('profile.travelerFallback'),
                })}
              </h1>
              <p className="text-xl text-yellow-50 mb-6 max-w-2xl">
                {t('profile.heroSubtitle')}
              </p>
            </div>
            <div className="relative flex justify-center lg:justify-end">
              <img
                src={airportImage}
                alt="Profile"
                className="w-full max-w-md h-auto rounded-2xl shadow-2xl border border-white/20"
              />
            </div>
          </div>
        </div>
        <div
          className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-white/20 to-transparent"
          style={{ zIndex: 1 }}
        ></div>
      </section>

      <main className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 pt-16 pb-20 space-y-12">
        <section className="rounded-3xl bg-white dark:bg-slate-900 shadow-lg border border-gray-100 dark:border-slate-800 p-8 grid grid-cols-1 lg:grid-cols-[auto,1fr] gap-8 items-center">
          <div className="flex flex-col items-center">
            {profilePhoto ? (
              <img
                src={profilePhoto}
                alt={displayName}
                className="h-24 w-24 rounded-full object-cover border-4 border-white shadow-lg"
              />
            ) : (
              <div className="h-24 w-24 rounded-full bg-black dark:bg-slate-700 text-yellow-400 flex items-center justify-center text-3xl font-black">
                {initials}
              </div>
            )}
            <p className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
              {displayName}
            </p>
            <p className="text-sm text-gray-500 dark:text-slate-400">
              {user?.email}
            </p>
            {profile?.createdAt ? (
              <p className="mt-2 text-xs font-semibold uppercase tracking-[0.2em] text-gray-400 dark:text-slate-500">
                {t('profile.memberSince', {
                  date: formatDate(profile.createdAt),
                })}
              </p>
            ) : null}
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800 p-4">
                <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-[0.3em]">
                  {t('profile.verification')}
                </p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">
                  {profile?.verificationStatus
                    ? profile.verificationStatus === 'verified'
                      ? t('profile.verified')
                      : profile.verificationStatus === 'pending'
                      ? t('profile.pending')
                      : t('profile.notVerified')
                    : t('profile.notVerified')}
                </p>
                <p className="text-xs text-gray-500 dark:text-slate-400">
                  {profile?.verificationStatus === 'verified'
                    ? t('profile.verifiedDescription')
                    : profile?.verificationStatus === 'pending'
                    ? t('profile.pendingDescription')
                    : t('profile.notVerifiedDescription')}
                </p>
              </div>
              <div className="rounded-2xl border border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800 p-4">
                <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-[0.3em]">
                  {t('profile.averageRating')}
                </p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">
                  {profile?.averageRating
                    ? profile.averageRating.toFixed(1)
                    : '—'}
                </p>
                <p className="text-xs text-gray-500 dark:text-slate-400">
                  {t('profile.reviewsCount', {
                    count: profile?.totalReviews || 0,
                  })}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                {
                  label: t('profile.senderCompleted'),
                  value: stats.senderCompleted,
                },
                {
                  label: t('profile.travelerCompleted'),
                  value: stats.travelerCompleted,
                },
              ].map(item => (
                <div
                  key={item.label}
                  className="rounded-2xl border border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 text-center"
                >
                  <p className="text-2xl font-black text-gray-900 dark:text-white">
                    {item.value}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    {item.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <button
            onClick={() => navigate('/profile/edit')}
            className="rounded-3xl border border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 text-left shadow hover:shadow-lg transition"
          >
            <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-[0.3em]">
              {t('profile.manage')}
            </p>
            <p className="text-2xl font-black text-gray-900 dark:text-white mb-2">
              {t('profile.editProfile')}
            </p>
            <p className="text-sm text-gray-500 dark:text-slate-400">
              {t('profile.editProfileDescription')}
            </p>
          </button>

          <button
            onClick={() =>
              navigate(`/profile/detail/${user?.uid}`, {
                state: { fallbackName: displayName },
              })
            }
            className="rounded-3xl border border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 text-left shadow hover:shadow-lg transition"
          >
            <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-[0.3em]">
              {t('profile.publicView')}
            </p>
            <p className="text-2xl font-black text-gray-900 dark:text-white mb-2">
              {t('profile.viewProfile')}
            </p>
            <p className="text-sm text-gray-500 dark:text-slate-400">
              {t('profile.viewProfileDescription')}
            </p>
          </button>
        </section>

        <section className="rounded-3xl bg-white dark:bg-slate-900 shadow-lg border border-gray-100 dark:border-slate-800 p-6 flex flex-wrap gap-4">
          <button
            onClick={() => navigate('/wallet')}
            className="rounded-2xl border border-gray-200 dark:border-slate-700 px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white hover:border-yellow-300 dark:hover:border-yellow-500 transition"
          >
            {t('profile.openWallet')}
          </button>
          <button
            onClick={() => navigate('/settings')}
            className="rounded-2xl border border-gray-200 dark:border-slate-700 px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white hover:border-yellow-300 dark:hover:border-yellow-500 transition"
          >
            {t('profile.settingsAndLegal')}
          </button>
          <button
            onClick={() => navigate('/notifications')}
            className="rounded-2xl border border-gray-200 dark:border-slate-700 px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white hover:border-yellow-300 dark:hover:border-yellow-500 transition"
          >
            {t('profile.notifications')}
          </button>
          <button
            onClick={() => navigate('/profile/support')}
            className="rounded-2xl border border-gray-200 dark:border-slate-700 px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white hover:border-yellow-300 dark:hover:border-yellow-500 transition"
          >
            {t('profile.supportTitle') || 'Support'}
          </button>
        </section>

        {/* Verification Section */}
        <section className="rounded-3xl bg-white dark:bg-slate-900 shadow-lg border border-gray-100 dark:border-slate-800 p-8">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="h-6 w-6 text-yellow-500" />
            <h2 className="text-2xl font-black text-gray-900 dark:text-white">
              {t('profile.identityVerification')}
            </h2>
          </div>

          {/* Verification Status */}
          {profile?.verificationStatus && (
            <div className="mb-6 p-4 rounded-2xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-600 dark:text-slate-400 uppercase tracking-[0.2em] mb-1">
                    {t('profile.status')}
                  </p>
                  <p
                    className={`text-lg font-bold ${
                      profile.verificationStatus === 'verified'
                        ? 'text-green-600 dark:text-green-400'
                        : profile.verificationStatus === 'pending'
                        ? 'text-yellow-600 dark:text-yellow-400'
                        : 'text-red-600 dark:text-red-400'
                    }`}
                  >
                    {profile.verificationStatus === 'verified'
                      ? t('profile.verified')
                      : profile.verificationStatus === 'pending'
                      ? t('profile.pendingReview')
                      : profile.verificationStatus === 'rejected'
                      ? t('profile.rejected')
                      : t('profile.notVerified')}
                  </p>
                </div>
                {profile.verificationStatus === 'verified' && (
                  <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
                )}
              </div>
              {profile.verificationRejectionComment && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                  {profile.verificationRejectionComment}
                </p>
              )}
            </div>
          )}

          {/* Document Type Selection - Only show if not verified and not pending */}
          {profile?.verificationStatus !== 'verified' &&
            profile?.verificationStatus !== 'pending' && (
              <>
                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-600 dark:text-slate-400 mb-3">
                    {t('profile.selectDocumentType')}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                      { value: 'passport', label: t('profile.passport') },
                      { value: 'idCard', label: t('profile.idCard') },
                      {
                        value: 'driverLicence',
                        label: t('profile.driverLicence'),
                      },
                    ].map(type => (
                      <button
                        key={type.value}
                        onClick={() => {
                          setVerificationType(type.value);
                          setFrontFile(null);
                          setBackFile(null);
                          setFrontPreview(null);
                          setBackPreview(null);
                        }}
                        className={`rounded-xl border-2 p-4 text-center transition ${
                          verificationType === type.value
                            ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400'
                            : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-300 hover:border-yellow-300 dark:hover:border-yellow-600'
                        }`}
                      >
                        <FileText className="h-6 w-6 mx-auto mb-2" />
                        <p className="text-sm font-semibold">{type.label}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* File Upload Section - Only show if type is selected */}
                {verificationType && (
                  <div className="space-y-6">
                    {/* Front Side Upload */}
                    <div>
                      <p className="text-sm font-semibold text-gray-600 dark:text-slate-400 mb-3">
                        {t('profile.frontSide')}
                      </p>
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <input
                            ref={frontFileInputRef}
                            type="file"
                            accept="image/*,.pdf"
                            className="hidden"
                            onChange={e => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setFrontFile(file);
                                if (file.type.startsWith('image/')) {
                                  const reader = new FileReader();
                                  reader.onloadend = () => {
                                    setFrontPreview(reader.result);
                                  };
                                  reader.readAsDataURL(file);
                                } else {
                                  setFrontPreview(null);
                                }
                              }
                            }}
                          />
                          <button
                            onClick={() => frontFileInputRef.current?.click()}
                            disabled={uploading || submitting}
                            className="w-full rounded-xl border-2 border-dashed border-gray-300 dark:border-slate-700 p-6 text-center hover:border-yellow-400 dark:hover:border-yellow-500 transition disabled:opacity-50"
                          >
                            {frontPreview ? (
                              <div className="space-y-2">
                                {frontFile?.type?.startsWith('image/') ? (
                                  <img
                                    src={frontPreview}
                                    alt="Front preview"
                                    className="mx-auto max-h-32 rounded-lg"
                                  />
                                ) : (
                                  <FileText className="h-12 w-12 mx-auto text-gray-400" />
                                )}
                                <p className="text-sm text-gray-600 dark:text-slate-400">
                                  {frontFile?.name}
                                </p>
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    setFrontFile(null);
                                    setFrontPreview(null);
                                    if (frontFileInputRef.current) {
                                      frontFileInputRef.current.value = '';
                                    }
                                  }}
                                  className="text-xs text-red-600 dark:text-red-400 hover:underline"
                                >
                                  {t('common.remove')}
                                </button>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                <Upload className="h-8 w-8 mx-auto text-gray-400" />
                                <p className="text-sm text-gray-600 dark:text-slate-400">
                                  {t('profile.clickToUploadFront')}
                                </p>
                              </div>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Back Side Upload */}
                    <div>
                      <p className="text-sm font-semibold text-gray-600 dark:text-slate-400 mb-3">
                        {t('profile.backSide')}
                      </p>
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <input
                            ref={backFileInputRef}
                            type="file"
                            accept="image/*,.pdf"
                            className="hidden"
                            onChange={e => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setBackFile(file);
                                if (file.type.startsWith('image/')) {
                                  const reader = new FileReader();
                                  reader.onloadend = () => {
                                    setBackPreview(reader.result);
                                  };
                                  reader.readAsDataURL(file);
                                } else {
                                  setBackPreview(null);
                                }
                              }
                            }}
                          />
                          <button
                            onClick={() => backFileInputRef.current?.click()}
                            disabled={uploading || submitting}
                            className="w-full rounded-xl border-2 border-dashed border-gray-300 dark:border-slate-700 p-6 text-center hover:border-yellow-400 dark:hover:border-yellow-500 transition disabled:opacity-50"
                          >
                            {backPreview ? (
                              <div className="space-y-2">
                                {backFile?.type?.startsWith('image/') ? (
                                  <img
                                    src={backPreview}
                                    alt="Back preview"
                                    className="mx-auto max-h-32 rounded-lg"
                                  />
                                ) : (
                                  <FileText className="h-12 w-12 mx-auto text-gray-400" />
                                )}
                                <p className="text-sm text-gray-600 dark:text-slate-400">
                                  {backFile?.name}
                                </p>
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    setBackFile(null);
                                    setBackPreview(null);
                                    if (backFileInputRef.current) {
                                      backFileInputRef.current.value = '';
                                    }
                                  }}
                                  className="text-xs text-red-600 dark:text-red-400 hover:underline"
                                >
                                  {t('common.remove')}
                                </button>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                <Upload className="h-8 w-8 mx-auto text-gray-400" />
                                <p className="text-sm text-gray-600 dark:text-slate-400">
                                  {t('profile.clickToUploadBack')}
                                </p>
                              </div>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <button
                      onClick={handleSubmitVerification}
                      disabled={
                        !frontFile || !backFile || uploading || submitting
                      }
                      className="w-full rounded-xl bg-yellow-500 hover:bg-yellow-600 dark:bg-yellow-600 dark:hover:bg-yellow-700 text-black font-bold py-3 px-6 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting
                        ? t('profile.submitting')
                        : uploading
                        ? t('profile.uploading')
                        : t('profile.submitForVerification')}
                    </button>
                  </div>
                )}
              </>
            )}
        </section>

        {/* Driver Verification Section - Only in Flexy Ride mode */}
        {isRideMode && (
          <section className="rounded-3xl bg-white dark:bg-slate-900 shadow-lg border border-gray-100 dark:border-slate-800 p-8">
            <div className="flex items-center gap-3 mb-6">
              <Car className="h-6 w-6 text-yellow-500" />
              <h2 className="text-2xl font-black text-gray-900 dark:text-white">
                {t('profile.driverVerification', {
                  defaultValue: 'Driver Verification',
                })}
              </h2>
            </div>

            <p className="text-sm text-gray-600 dark:text-slate-400 mb-6 italic">
              {t('profile.driverVerificationNote', {
                defaultValue: 'If you are a driver, please verify your documents',
              })}
            </p>

            {/* Driver Verification Status */}
            {profile?.driverVerificationStatus && (
              <div className="mb-6 p-4 rounded-2xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-600 dark:text-slate-400 uppercase tracking-[0.2em] mb-1">
                      {t('profile.driverVerificationStatus', {
                        defaultValue: 'Driver Verification Status',
                      })}
                    </p>
                    <p
                      className={`text-lg font-bold ${
                        profile.driverVerificationStatus === 'verified'
                          ? 'text-green-600 dark:text-green-400'
                          : profile.driverVerificationStatus === 'pending'
                          ? 'text-yellow-600 dark:text-yellow-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}
                    >
                      {profile.driverVerificationStatus === 'verified'
                        ? t('profile.verified')
                        : profile.driverVerificationStatus === 'pending'
                        ? t('profile.pendingReview')
                        : t('profile.notVerified')}
                    </p>
                  </div>
                  {profile.driverVerificationStatus === 'verified' && (
                    <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
                  )}
                </div>
              </div>
            )}

            {/* Driver Verification File Upload - Only show if not verified and not pending */}
            {profile?.driverVerificationStatus !== 'verified' &&
              profile?.driverVerificationStatus !== 'pending' && (
                <div className="space-y-6">
                  {/* Driver License Front */}
                  <div>
                    <p className="text-sm font-semibold text-gray-600 dark:text-slate-400 mb-3">
                      {t('profile.driverLicenseFront', {
                        defaultValue: 'Driver License (Front)',
                      })}
                    </p>
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <input
                          ref={driverLicenseFrontInputRef}
                          type="file"
                          accept="image/*,.pdf"
                          className="hidden"
                          onChange={e => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setDriverLicenseFront(file);
                              if (file.type.startsWith('image/')) {
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setDriverLicenseFrontPreview(reader.result);
                                };
                                reader.readAsDataURL(file);
                              } else {
                                setDriverLicenseFrontPreview(null);
                              }
                            }
                          }}
                        />
                        <button
                          onClick={() => driverLicenseFrontInputRef.current?.click()}
                          disabled={uploadingDriverVerification || submittingDriverVerification}
                          className="w-full rounded-xl border-2 border-dashed border-gray-300 dark:border-slate-700 p-6 text-center hover:border-yellow-400 dark:hover:border-yellow-500 transition disabled:opacity-50"
                        >
                          {driverLicenseFrontPreview ? (
                            <div className="space-y-2">
                              {driverLicenseFront?.type?.startsWith('image/') ? (
                                <img
                                  src={driverLicenseFrontPreview}
                                  alt="Driver license front preview"
                                  className="mx-auto max-h-32 rounded-lg"
                                />
                              ) : (
                                <FileText className="h-12 w-12 mx-auto text-gray-400" />
                              )}
                              <p className="text-sm text-gray-600 dark:text-slate-400">
                                {driverLicenseFront?.name}
                              </p>
                              <button
                                onClick={e => {
                                  e.stopPropagation();
                                  setDriverLicenseFront(null);
                                  setDriverLicenseFrontPreview(null);
                                  if (driverLicenseFrontInputRef.current) {
                                    driverLicenseFrontInputRef.current.value = '';
                                  }
                                }}
                                className="text-xs text-red-600 dark:text-red-400 hover:underline"
                              >
                                {t('common.remove')}
                              </button>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <Upload className="h-8 w-8 mx-auto text-gray-400" />
                              <p className="text-sm text-gray-600 dark:text-slate-400">
                                {t('profile.clickToUploadFront')}
                              </p>
                            </div>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Driver License Back */}
                  <div>
                    <p className="text-sm font-semibold text-gray-600 dark:text-slate-400 mb-3">
                      {t('profile.driverLicenseBack', {
                        defaultValue: 'Driver License (Back)',
                      })}
                    </p>
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <input
                          ref={driverLicenseBackInputRef}
                          type="file"
                          accept="image/*,.pdf"
                          className="hidden"
                          onChange={e => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setDriverLicenseBack(file);
                              if (file.type.startsWith('image/')) {
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setDriverLicenseBackPreview(reader.result);
                                };
                                reader.readAsDataURL(file);
                              } else {
                                setDriverLicenseBackPreview(null);
                              }
                            }
                          }}
                        />
                        <button
                          onClick={() => driverLicenseBackInputRef.current?.click()}
                          disabled={uploadingDriverVerification || submittingDriverVerification}
                          className="w-full rounded-xl border-2 border-dashed border-gray-300 dark:border-slate-700 p-6 text-center hover:border-yellow-400 dark:hover:border-yellow-500 transition disabled:opacity-50"
                        >
                          {driverLicenseBackPreview ? (
                            <div className="space-y-2">
                              {driverLicenseBack?.type?.startsWith('image/') ? (
                                <img
                                  src={driverLicenseBackPreview}
                                  alt="Driver license back preview"
                                  className="mx-auto max-h-32 rounded-lg"
                                />
                              ) : (
                                <FileText className="h-12 w-12 mx-auto text-gray-400" />
                              )}
                              <p className="text-sm text-gray-600 dark:text-slate-400">
                                {driverLicenseBack?.name}
                              </p>
                              <button
                                onClick={e => {
                                  e.stopPropagation();
                                  setDriverLicenseBack(null);
                                  setDriverLicenseBackPreview(null);
                                  if (driverLicenseBackInputRef.current) {
                                    driverLicenseBackInputRef.current.value = '';
                                  }
                                }}
                                className="text-xs text-red-600 dark:text-red-400 hover:underline"
                              >
                                {t('common.remove')}
                              </button>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <Upload className="h-8 w-8 mx-auto text-gray-400" />
                              <p className="text-sm text-gray-600 dark:text-slate-400">
                                {t('profile.clickToUploadBack')}
                              </p>
                            </div>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Vehicle Insurance */}
                  <div>
                    <p className="text-sm font-semibold text-gray-600 dark:text-slate-400 mb-3">
                      {t('profile.vehicleInsurance', {
                        defaultValue: 'Vehicle Insurance Policy',
                      })}
                    </p>
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <input
                          ref={vehicleInsuranceInputRef}
                          type="file"
                          accept="image/*,.pdf"
                          className="hidden"
                          onChange={e => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setVehicleInsurance(file);
                              if (file.type.startsWith('image/')) {
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setVehicleInsurancePreview(reader.result);
                                };
                                reader.readAsDataURL(file);
                              } else {
                                setVehicleInsurancePreview(null);
                              }
                            }
                          }}
                        />
                        <button
                          onClick={() => vehicleInsuranceInputRef.current?.click()}
                          disabled={uploadingDriverVerification || submittingDriverVerification}
                          className="w-full rounded-xl border-2 border-dashed border-gray-300 dark:border-slate-700 p-6 text-center hover:border-yellow-400 dark:hover:border-yellow-500 transition disabled:opacity-50"
                        >
                          {vehicleInsurancePreview ? (
                            <div className="space-y-2">
                              {vehicleInsurance?.type?.startsWith('image/') ? (
                                <img
                                  src={vehicleInsurancePreview}
                                  alt="Vehicle insurance preview"
                                  className="mx-auto max-h-32 rounded-lg"
                                />
                              ) : (
                                <FileText className="h-12 w-12 mx-auto text-gray-400" />
                              )}
                              <p className="text-sm text-gray-600 dark:text-slate-400">
                                {vehicleInsurance?.name}
                              </p>
                              <button
                                onClick={e => {
                                  e.stopPropagation();
                                  setVehicleInsurance(null);
                                  setVehicleInsurancePreview(null);
                                  if (vehicleInsuranceInputRef.current) {
                                    vehicleInsuranceInputRef.current.value = '';
                                  }
                                }}
                                className="text-xs text-red-600 dark:text-red-400 hover:underline"
                              >
                                {t('common.remove')}
                              </button>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <Upload className="h-8 w-8 mx-auto text-gray-400" />
                              <p className="text-sm text-gray-600 dark:text-slate-400">
                                {t('profile.uploadFile', {
                                  defaultValue: 'Click to upload file',
                                })}
                              </p>
                            </div>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    onClick={handleSubmitDriverVerification}
                    disabled={
                      !driverLicenseFront ||
                      !driverLicenseBack ||
                      !vehicleInsurance ||
                      uploadingDriverVerification ||
                      submittingDriverVerification
                    }
                    className="w-full rounded-xl bg-yellow-500 hover:bg-yellow-600 dark:bg-yellow-600 dark:hover:bg-yellow-700 text-black font-bold py-3 px-6 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submittingDriverVerification
                      ? t('profile.submitting')
                      : uploadingDriverVerification
                      ? t('profile.uploading')
                      : t('profile.submitDriverVerification', {
                          defaultValue: 'Submit Driver Verification',
                        })}
                  </button>
                </div>
              )}

            {/* Pending Status Message */}
            {profile?.driverVerificationStatus === 'pending' && (
              <div className="mt-6 p-4 rounded-2xl border border-yellow-300 dark:border-yellow-600 bg-yellow-50 dark:bg-yellow-900/20">
                <p className="text-sm text-yellow-800 dark:text-yellow-300 text-center font-semibold">
                  {t('profile.pending')} -{' '}
                  {t('profile.driverVerificationSubmitted', {
                    defaultValue: 'Driver verification submitted',
                  })}
                </p>
              </div>
            )}
          </section>
        )}

        {loading && (
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-6 shadow border border-gray-100 dark:border-slate-800 text-center text-gray-500 dark:text-slate-400">
            {t('profile.loadingLatest')}
          </div>
        )}
      </main>
    </div>
  );
}
